import type { FilterSelectionMode } from '@api/api/screener';
import type {
  IncludeExcludeLabelProps,
  IncludeExcludeLabels,
} from '@terminal/components/filter/include-exclude/include-exclude.types';
import type { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import type { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';

export const INCLUDE_EXCLUDE_SELECTION_MODES: FilterSelectionMode[] = [
  'include',
  'exclude',
];

export const getDefaultFilterOption = (
  options: FilterOption[],
  defaultOptionKey?: string
) => options.find(option => option.key === defaultOptionKey);

export const getFilterOptionGroupBySelectionMode = (
  filterOptionGroups: ActiveFilterOptions[],
  selectionMode: FilterSelectionMode
) =>
  filterOptionGroups.find(
    group => (group.selectionMode ?? 'include') === selectionMode
  );

export const getFilterOptionsBySelectionMode = (
  filterOptionGroups: ActiveFilterOptions[]
) => ({
  includedOptions:
    getFilterOptionGroupBySelectionMode(filterOptionGroups, 'include')
      ?.options ?? [],
  excludedOptions:
    getFilterOptionGroupBySelectionMode(filterOptionGroups, 'exclude')
      ?.options ?? [],
});

export const getIncludeExcludeButtonLabels = (
  labels: IncludeExcludeLabels,
  includedOptions: ActiveFilterOptions['options'],
  excludedOptions: ActiveFilterOptions['options']
) =>
  INCLUDE_EXCLUDE_SELECTION_MODES.map(selectionMode => {
    const count =
      selectionMode === 'include'
        ? includedOptions.length
        : excludedOptions.length;
    return labels[selectionMode](count || undefined);
  });

export const getIncludeExcludeLabel = ({
  includedOptions,
  excludedOptions,
  labels,
}: IncludeExcludeLabelProps) => {
  const totalCount = includedOptions.length + excludedOptions.length;

  if (totalCount === 1) {
    return includedOptions[0]
      ? labels.is(includedOptions[0].name)
      : labels.isNot(excludedOptions[0].name);
  }

  if (includedOptions.length === 1 && excludedOptions.length === 1) {
    return labels.combined(
      labels.is(includedOptions[0].name),
      labels.isNot(excludedOptions[0].name)
    );
  }

  const includedLabel = includedOptions.length
    ? labels.included(includedOptions.length)
    : '';
  const excludedLabel = excludedOptions.length
    ? labels.excluded(excludedOptions.length)
    : '';

  if (includedLabel && excludedLabel) {
    return labels.combined(includedLabel, excludedLabel);
  }

  return includedLabel || excludedLabel;
};
